import { getboards, testCoins, pushDeviceFotaUpgrade, pushDeviceFotaUrlUpgrade, getDeviceUartConfig, setDeviceUartConfig, rebootDevice, powerCtrlDevice, getDeviceBindHistory } from '@/api/board'

const boardsModule = {
  namespaced: true,

  state: {
    devices: [], // 保存设备列表
    totalCount: 0, // 总设备数
    currentPage: 1, // 当前页码
    pageSize: 20, // 每页设备数
    isLoading: false, // 加载状态
    lastUpdated: null, // 数据最后更新时间
    testCoinsStatus: null, // 保存 testCoins 请求的状态（可以根据实际需求进行修改）
    uartConfigs: {},
  },

  getters: {
    // 获取所有设备
    getAllDevices(state) {
      return state.devices;
    },
    // 获取分页信息
    getPagination(state) {
      return {
        currentPage: state.currentPage,
        pageSize: state.pageSize,
        totalCount: state.totalCount,
        totalPages: Math.ceil(state.totalCount / state.pageSize)
      };
    },
    // 判断设备列表是否为空
    isDevicesEmpty(state) {
      return state.devices.length === 0;
    },
    // 获取加载状态
    getLoadingStatus(state) {
      return state.isLoading;
    },
    // 获取 testCoins 请求的状态
    getTestCoinsStatus(state) {
      return state.testCoinsStatus;
    },
    getUartConfigByBoardId: (state) => (boardId) => {
      if (!boardId) {
        return null;
      }
      return state.uartConfigs[boardId] || null;
    },
  },

  mutations: {
    // 设置设备列表数据
    SET_DEVICES(state, { devices, totalCount, page, pageSize }) {
      if (!Array.isArray(devices)) {
          console.error('Invalid devices data:', devices);
          return;
      }
      state.devices = devices;
      state.totalCount = totalCount || 0;
      state.currentPage = page || 1;
      state.pageSize = pageSize || 20;
      state.lastUpdated = Date.now(); // 更新数据的最后更新时间
      state.isLoading = false; // 结束加载状态
    },
    // 更新分页信息
    SET_PAGINATION(state, { page, pageSize }) {
      if (page) state.currentPage = page;
      if (pageSize) state.pageSize = pageSize;
    },
    // 设置加载状态
    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading;
    },
    // 设置 testCoins 请求状态
    SET_TEST_COINS_STATUS(state, status) {
      state.testCoinsStatus = status;
    },
    SET_UART_CONFIG(state, { boardId, config }) {
      if (!boardId) {
        return;
      }
      state.uartConfigs = {
        ...state.uartConfigs,
        [boardId]: config || null,
      };
    },
  },

  actions: {
    // 获取所有设备（分页版本）
    async fetchDevices({ commit }, { page = 1, pageSize = 20, board_id, deliver_id, online } = {}) {
      commit('SET_LOADING', true);
      try {
        const params = { page, page_size: pageSize };
        if (board_id) params.board_id = board_id;
        if (deliver_id) params.deliver_id = deliver_id;
        if (online !== undefined && online !== -1) params.online = online;
        const response = await getboards(params);
        // 适配新的分页响应格式
        if (response && response.code === 0 && response.desc) {
          const data = response.desc;
          const devices = data.devices || data.boards || [];
          const totalCount = data.total_count || 0;
          const currentPage = data.page || page;
          const currentPageSize = data.page_size || pageSize;

          commit('SET_DEVICES', {
            devices,
            totalCount,
            page: currentPage,
            pageSize: currentPageSize
          });

          // 更新本地缓存，并存储当前时间戳
          localStorage.setItem('devices', JSON.stringify(devices));
          localStorage.setItem('devices_timestamp', Date.now().toString());
        } else {
          console.error('Invalid response format:', response);
          commit('SET_DEVICES', { devices: [], totalCount: 0, page: 1, pageSize: 20 });
        }
      } catch (error) {
        console.error('获取设备列表失败', error);
        commit('SET_LOADING', false); // 失败时停止加载
      }
    },

    // 清除本地缓存
    clearLocalCache({ commit }) {
      localStorage.removeItem('devices');
      localStorage.removeItem('devices_timestamp'); // 清除时间戳
      commit('SET_DEVICES', { devices: [], totalCount: 0, page: 1, pageSize: 20 }); // 清空设备列表
    },

    async testCoinsRequest({ commit }, { board_id, coins, downmsg = null, port }) {
      commit('SET_LOADING', true); // 设置为加载中
      commit('SET_TEST_COINS_STATUS', null); // 清除之前的状态

      try {
        const response = await testCoins({ board_id, coins, downmsg, port });
        console.log('response from server:', response);
        if (response && response.code === 0) { // 根据实际 API 返回结构调整
          commit('SET_TEST_COINS_STATUS', 'success');
          return response; // 返回 response，以便在 submitTestCoin 中使用
        } else {
          commit('SET_TEST_COINS_STATUS', 'failure');
          return response; // 返回 response，以便在 submitTestCoin 中使用
        }
      } catch (error) {
        console.error('testCoins 请求失败', error);
        commit('SET_TEST_COINS_STATUS', 'error');
        throw error; // 抛出错误，以便在 submitTestCoin 中捕获
      } finally {
        commit('SET_LOADING', false); // 停止加载
      }
    },

    async pushFotaRequest({ commit }, { board_id, oss_name }) {
      commit('SET_LOADING', true)
      try {
        const response = await pushDeviceFotaUpgrade({ board_id, oss_name })
        return response
      } catch (error) {
        console.error('push fota 请求失败', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async pushFotaUrlRequest({ commit }, { board_id, fota_url }) {
      commit('SET_LOADING', true)
      try {
        const response = await pushDeviceFotaUrlUpgrade({ board_id, fota_url })
        return response
      } catch (error) {
        console.error('push fota url 请求失败', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async rebootDeviceRequest({ commit }, { board_id, port_id }) {
      commit('SET_LOADING', true)
      try {
        const response = await rebootDevice({ board_id, port_id: port_id || 1 })
        return response
      } catch (error) {
        console.error('远程重启请求失败', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchUartConfig({ commit }, { board_id, force = false }) {
      if (!board_id) {
        return null;
      }
      const cacheKey = `uart_config_${board_id}`;
      const cacheTsKey = `uart_config_${board_id}_ts`;
      const cachedConfig = localStorage.getItem(cacheKey);
      const cachedTimestamp = localStorage.getItem(cacheTsKey);
      const isCacheValid = cachedConfig && cachedTimestamp && (Date.now() - parseInt(cachedTimestamp)) < 5 * 60 * 1000;

      if (!force && isCacheValid) {
        const config = JSON.parse(cachedConfig);
        commit('SET_UART_CONFIG', { boardId: board_id, config });
        return { code: 0, desc: config };
      }
      try {
        const response = await getDeviceUartConfig({ board_id, force: force ? 1 : 0 });
        if (response && response.code === 0) {
          localStorage.setItem(cacheKey, JSON.stringify(response.desc));
          localStorage.setItem(cacheTsKey, Date.now().toString());
          commit('SET_UART_CONFIG', { boardId: board_id, config: response.desc });
        }
        return response;
      } catch (error) {
        console.error('获取串口配置失败', error);
        throw error;
      }
    },

    async setUartConfig({ commit, dispatch }, payload) {
      if (!payload || !payload.board_id) {
        return null;
      }
      commit('SET_LOADING', true)
      try {
        const response = await setDeviceUartConfig(payload);
        if (response && response.code === 0) {
          await dispatch('fetchUartConfig', { board_id: payload.board_id, force: true });
        }
        return response;
      } catch (error) {
        console.error('设置串口配置失败', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async powerCtrlDeviceRequest({ commit }, { board_id, action, port_id }) {
      commit('SET_LOADING', true)
      try {
        const response = await powerCtrlDevice({ board_id, action, port_id: port_id || 1 })
        return response
      } catch (error) {
        console.error('远程开关机请求失败', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchDeviceBindHistory({ commit }, { board_id }) {
      commit('SET_LOADING', true)
      try {
        const response = await getDeviceBindHistory({ board_id })
        return response
      } catch (error) {
        console.error('获取设备绑定历史失败', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

  },
}

export default boardsModule;
